import type { ReactNode } from 'react';
import { richTextPlainText } from '../../lib/rich-text';
import {
  getStatusMeta,
  isTerminalStatus,
  type Task,
  type TaskStatusMeta,
  type WorkspaceMember,
} from '../../lib/task-list';
import { cn } from '../../lib/utils';
import {
  TaskAssigneeAvatar,
  TaskCompletionToggle,
  TaskContactChip,
  TaskDueDate,
  TaskPriorityBadge,
  TaskStatusBadge,
} from './task-meta';

export interface TaskCardProps {
  task: Task;
  statuses: TaskStatusMeta[];
  members: WorkspaceMember[];
  onClick: () => void;
  /** When provided, renders a completion checkbox (e.g. Assignee view). Omit on drag surfaces (Kanban). */
  onToggleComplete?: (task: Task) => void;
  /** Show the workspace-status pill. Off for Kanban, where the column already is the status. */
  showStatus?: boolean;
  /** Color the top border by the task's status (Kanban board convention). */
  accentTop?: boolean;
  /**
   * Extra chips for the meta row, rendered after the due date and before the assignee.
   * For facts a surface tracks that the standard chips don't cover (the extension's
   * repeat/reminder schedule, say) — the card stays the same card either way.
   */
  metaSlot?: ReactNode;
  /**
   * A trailing control for the card — an overflow menu, a schedule button, a delete action.
   * The slot's own content is responsible for calling `stopPropagation`: the card body is
   * clickable, and a popover trigger in here needs to own its event handling rather than be
   * wrapped in a swallow-everything container.
   */
  actionSlot?: ReactNode;
  /**
   * Lines of plain-text body preview to show under the title. Default `0` — board surfaces
   * deliberately show none; the contact panel shows one.
   */
  descriptionLines?: 0 | 1 | 2;
  className?: string;
}

/**
 * The one task card used across board/grouped views (Kanban, Assignee, and any
 * future card surface). It composes the shared `task-meta` chips so every card
 * shows the same task facts — status, priority, linked contact, due date (with
 * overdue emphasis), and assignee — instead of each view hand-rolling a subset.
 * List rows use the same chips directly via `TaskListTable`.
 */
export function TaskCard({
  task,
  statuses,
  members,
  onClick,
  onToggleComplete,
  showStatus = true,
  accentTop = false,
  metaSlot,
  actionSlot,
  descriptionLines = 0,
  className,
}: TaskCardProps) {
  const done = isTerminalStatus(statuses, task.status);
  const statusMeta = getStatusMeta(statuses, task.status);
  const member = task.assignedTo ? (members.find((m) => m.id === task.assignedTo) ?? null) : null;
  const description =
    descriptionLines > 0 && task.content ? richTextPlainText(task.content).trim() : '';

  return (
    <div
      onClick={onClick}
      style={accentTop ? { borderTopColor: statusMeta?.color, borderTopWidth: 3 } : undefined}
      className={cn(
        'rounded-xl border border-border/50 bg-card p-3 shadow-sm transition-shadow hover:shadow-md cursor-pointer',
        done && 'opacity-70',
        className
      )}
    >
      <div className="flex items-start gap-2.5">
        {onToggleComplete && (
          <TaskCompletionToggle
            done={done}
            onToggle={() => onToggleComplete(task)}
            className="mt-0.5"
          />
        )}
        <div className="min-w-0 flex-1">
          <div
            className={cn(
              'text-[12.5px] font-semibold text-foreground line-clamp-2',
              done && 'line-through text-muted-foreground'
            )}
          >
            {task.label}
          </div>
          {description && (
            <p
              className={cn(
                'mt-1 text-[11px] text-muted-foreground',
                descriptionLines === 1 ? 'line-clamp-1' : 'line-clamp-2'
              )}
            >
              {description}
            </p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5 empty:mt-0">
            {showStatus && <TaskStatusBadge statuses={statuses} statusKey={task.status} />}
            <TaskPriorityBadge priority={task.priority} />
            <TaskContactChip contact={task.contact} />
            <TaskDueDate dueAt={task.dueAt} done={done} />
            {metaSlot}
            <TaskAssigneeAvatar member={member} className="ml-auto" />
          </div>
        </div>
        {actionSlot}
      </div>
    </div>
  );
}
